import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse, PaginatedApiResponse, USE_ALL_SITES } from '@card-software/nexus-utilities';
import { Business, BusinessSearchCommand } from '../models/business';
import { ProviderDetails } from '../models/provider/provider-details';

@Injectable({ providedIn: 'root' })
export class BusinessProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/business';

  public createBusiness(data: Business): Observable<ApiResponse<Business>> {
    return this.httpClient.post<ApiResponse<Business>>(this.baseUrl, data);
  }

  public updateBusiness(id: string, data: Business): Observable<ApiResponse<Business>> {
    return this.httpClient.put<ApiResponse<Business>>(this.baseUrl + '/' + id, data);
  }

  public getBusinessById(id: string): Observable<ApiResponse<Business>> {
    return this.httpClient.get<ApiResponse<Business>>(this.baseUrl + '/' + id);
  }

  /**
   * Saves the provider block on its own (#4892). The Provider Details page never loads the rest of the
   * business, so it must not round-trip the whole document — this PATCH touches ProviderDetails only.
   * The API answers with the stored block, credit days and AP account name already derived.
   */
  public updateProviderDetails(id: string, providerDetails: ProviderDetails): Observable<ApiResponse<ProviderDetails>> {
    return this.httpClient.patch<ApiResponse<ProviderDetails>>(`${this.baseUrl}/${id}/provider-details`, providerDetails);
  }

  public search(
    searchCommand: BusinessSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder?: number,
    matchAny = false,
    useAllSites = false,
  ): Observable<ApiResponse<PaginatedApiResponse<Business>>> {
    let url = `${this.baseUrl}/search?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      url += `&sortField=${sortField}&sortOrder=${sortOrder ?? -1}`;
    }
    const context = new HttpContext().set(USE_ALL_SITES, useAllSites);
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<Business>>>(url + `&matchAny=${matchAny}`, searchCommand, { context });
  }
}
